/**
 * The external dependencies
 */
/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Grid, // <--- Using Grid for layout
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

/**
 * The internal dependencies
 */
import { ChangeEvent } from 'react';
import {
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
} from '@codefluegel/zeta-change-typescript-client';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import theme from '../../theme';
import { useChangeStore } from '../../store/changeStore';
import { isChangeManager, isKTM } from '../../utils/permissionHelper';

export type ProcessAccordionFormProps = {
  process: ProcessListForChangeDto;
  handleChange: (fieldName: string, fieldValue: string | boolean) => void;
  workPackages: ListWorkPackageDto[];
  workServices: ListWorkServiceDto[];
  workMaterials: ListWorkMaterialDto[];
};

const ProcessAccordionForm = observer(
  ({
    process,
    handleChange,
    workPackages,
    workServices,
    workMaterials,
  }: ProcessAccordionFormProps) => {
    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { target } = e;
      const value =
        target.type === 'text' || target.type === 'textarea'
          ? target.value
          : target.checked;

      handleChange(target.name, value);
    };
    const onBlur = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { target } = e;
      const { value } = target;

      handleChange(target.name, value);
    };

    const { t } = useTranslation();

    const { change } = useChangeStore();

    const isKTMorCM = isKTM(change, process.pId) || isChangeManager(change);

    const workPackagesAvailable = workPackages.length > 0;
    const workServicesAvailable = workServices.length > 0;
    const workMaterialsAvailable = workMaterials.length > 0;

    return (
      <Box sx={{ p: 2 }}>
        {/* Main Grid Container for the two-column layout */}
        <Grid container spacing={4}>
          {/* Affected Block - Grid Item 1 */}
          <Grid>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={process.affected}
                    name="affected"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    /* @ts-ignore */
                    disabled={
                      process.evaluationStatus === 'APPROVED' ||
                      !isKTMorCM ||
                      workPackagesAvailable ||
                      process.plannedMaterial ||
                      process.timeImpact ||
                      process.affectedDescription ||
                      workServicesAvailable ||
                      workMaterialsAvailable
                    }
                    onChange={onChange}
                  />
                }
                label={t('process.affectedBy')}
              />
            </FormGroup>
            <TextField
              rows={2}
              name="affectedDescription"
              disabled={
                process.evaluationStatus === 'APPROVED' ||
                !process.affected ||
                !isKTMorCM
              }
              defaultValue={process.affectedDescription || ''}
              onBlur={onBlur}
              css={css`
                width: 100%;
                background: white .MuiInput-underline:before {
                  border-bottom: 1px solid #ececec;
                }

                .Mui-disabled {
                  cursor: not-allowed;
                }
              `}
              label={t('process.affectedDesc')}
              variant="standard"
            />
          </Grid>

          {/* Time Impact Block - Grid Item 2 */}
          <Grid>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={
                      process.evaluationStatus === 'APPROVED' ||
                      !isKTMorCM ||
                      process.affected
                    }
                    checked={process.timeImpact}
                    name="timeImpact"
                    onChange={onChange}
                  />
                }
                label={t('process.timeMaterial')}
              />
            </FormGroup>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AccessTimeIcon
                css={css`
                  color: ${theme.palette.primary.light};
                  width: 20px;
                  margin-right: 8px;
                  margin-top: 10px;
                `}
              />
              <TextField
                name="timeImpactDescription"
                disabled={
                  process.evaluationStatus === 'APPROVED' ||
                  !process.timeImpact ||
                  process.affected ||
                  !isKTMorCM
                }
                defaultValue={process.timeImpactDescription || ''}
                onChange={onChange}
                css={css`
                  width: 100%;

                  .MuiInput-underline:before {
                    border-bottom: 1px solid #ececec;
                  }

                  .Mui-disabled {
                    cursor: not-allowed;
                  }
                `}
                label={t('process.DescTime')}
                variant="standard"
                slotProps={{
                  inputLabel: {},
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  },
);

export default ProcessAccordionForm;
